/**
 * React API Hooks for ProGeoData
 * Custom hooks for API interactions with proper TypeScript types
 * Uses native fetch API with error handling and loading states
 */

import { useState, useEffect, useCallback } from 'react';
import type {
  SearchParams,
  SearchResponse,
  SourcesResponse,
  BulkExportRequest,
  BulkExportResponse,
  BulkExportStatusResponse,
  ApiError,
} from '../components/types/api.types';

// ============================================================================
// API CONFIGURATION
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
}

/**
 * Base fetch wrapper with error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add authorization header if API key exists in localStorage
  const apiKey = localStorage.getItem('progeodata_api_key');
  if (apiKey) {
    defaultHeaders['Authorization'] = `Bearer ${apiKey}`;
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: 'Network error',
      message: `HTTP ${response.status}: ${response.statusText}`,
      status: response.status,
    }));
    throw error;
  }

  return response.json();
}

// ============================================================================
// HOOK: USE SEARCH PROFESSIONALS
// ============================================================================

interface UseSearchProfessionalsResult {
  data: SearchResponse | null;
  isLoading: boolean;
  error: ApiError | null;
  search: (params: SearchParams) => Promise<void>;
  reset: () => void;
}

export function useSearchProfessionals(): UseSearchProfessionalsResult {
  const [data, setData] = useState<SearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const search = useCallback(async (params: SearchParams) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiFetch<SearchResponse>('/search', {
        method: 'POST',
        body: JSON.stringify(params),
      });
      setData(result);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return { data, isLoading, error, search, reset };
}

// ============================================================================
// HOOK: USE SOURCES (STATE/PROFESSION OPTIONS)
// ============================================================================

interface UseSourcesResult {
  data: SourcesResponse | null;
  isLoading: boolean;
  error: ApiError | null;
  refetch: () => Promise<void>;
}

export function useSources(): UseSourcesResult {
  const [data, setData] = useState<SourcesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchSources = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiFetch<SourcesResponse>('/sources');
      setData(result);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  return { data, isLoading, error, refetch: fetchSources };
}

// ============================================================================
// HOOK: USE BULK EXPORT
// ============================================================================

interface UseBulkExportResult {
  data: BulkExportResponse | null;
  isLoading: boolean;
  error: ApiError | null;
  createExport: (params: BulkExportRequest) => Promise<void>;
  reset: () => void;
}

export function useBulkExport(): UseBulkExportResult {
  const [data, setData] = useState<BulkExportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const createExport = useCallback(async (params: BulkExportRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiFetch<BulkExportResponse>('/bulk-export', {
        method: 'POST',
        body: JSON.stringify(params),
      });
      setData(result);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return { data, isLoading, error, createExport, reset };
}

// ============================================================================
// HOOK: USE BULK EXPORT STATUS (WITH AUTO-REFRESH)
// ============================================================================

interface UseBulkExportStatusOptions {
  jobId: string | null;
  autoRefresh?: boolean;
  refreshInterval?: number; // milliseconds
}

interface UseBulkExportStatusResult {
  data: BulkExportStatusResponse | null;
  isLoading: boolean;
  error: ApiError | null;
  refetch: () => Promise<void>;
}

export function useBulkExportStatus({
  jobId,
  autoRefresh = true,
  refreshInterval = 5000,
}: UseBulkExportStatusOptions): UseBulkExportStatusResult {
  const [data, setData] = useState<BulkExportStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!jobId) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await apiFetch<BulkExportStatusResponse>(
        `/bulk-export/${jobId}`
      );
      setData(result);
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  // Initial fetch
  useEffect(() => {
    if (jobId) {
      fetchStatus();
    }
  }, [jobId, fetchStatus]);

  // Auto-refresh while processing
  useEffect(() => {
    if (!autoRefresh || !jobId || !data?.job) return;

    // Stop refreshing if job is completed or failed
    const status = data.job.status;
    if (status === 'completed' || status === 'failed') return;

    const intervalId = setInterval(() => {
      fetchStatus();
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [autoRefresh, jobId, data, refreshInterval, fetchStatus]);

  return { data, isLoading, error, refetch: fetchStatus };
}

// ============================================================================
// HOOK: USE DOWNLOAD CSV
// ============================================================================

interface UseDownloadCsvResult {
  isDownloading: boolean;
  error: ApiError | null;
  downloadCsv: (jobId: string) => Promise<void>;
}

export function useDownloadCsv(): UseDownloadCsvResult {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const downloadCsv = useCallback(async (jobId: string) => {
    setIsDownloading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/bulk-export/${jobId}/download`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('progeodata_api_key')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `export-${jobId}.csv`;

      if (contentDisposition) {
        const matches = /filename="?([^"]+)"?/.exec(contentDisposition);
        if (matches?.[1]) {
          filename = matches[1];
        }
      }

      // Create blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError({
        error: 'Download failed',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsDownloading(false);
    }
  }, []);

  return { isDownloading, error, downloadCsv };
}
